import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ConfirmationModal.css';

interface ConfirmationModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel: () => void;
    isDestructive?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
    isDestructive = false
}) => {
    const dialogRef = useRef<HTMLDialogElement>(null);

    // Keep the native <dialog> element's open state in sync with `isOpen`.
    // showModal() renders it in the top layer with a native ::backdrop and
    // built-in focus trapping/Escape handling for better accessibility
    // than a manually managed div with role="dialog".
    useEffect(() => {
        const dialogEl = dialogRef.current;
        if (!dialogEl) return;

        if (isOpen) {
            if (!dialogEl.open) {
                dialogEl.showModal();
            }
        } else if (dialogEl.open) {
            dialogEl.close();
        }
    }, [isOpen]);

    useEffect(() => {
        const dialogEl = dialogRef.current;
        if (!dialogEl) return;

        // The native "cancel" event fires on Escape. Prevent the default
        // close so React stays the source of truth for `isOpen`, and let
        // the caller decide how to react.
        const handleCancel = (e: Event) => {
            e.preventDefault();
            onCancel();
        };

        // Clicking the native ::backdrop dispatches the click event with
        // the dialog itself as the target, so this only fires for clicks
        // outside the dialog's content box.
        const handleBackdropClick = (e: MouseEvent) => {
            if (e.target === dialogEl) {
                onCancel();
            }
        };

        // Escape is handled by the native "cancel" event above; Enter/Space
        // mirror backdrop-click dismiss when the dialog itself is focused.
        const handleBackdropKeyDown = (e: KeyboardEvent) => {
            if (
                (e.key === 'Enter' || e.key === ' ') &&
                e.target === dialogEl
            ) {
                e.preventDefault();
                onCancel();
            }
        };

        dialogEl.addEventListener('cancel', handleCancel);
        dialogEl.addEventListener('click', handleBackdropClick);
        dialogEl.addEventListener('keydown', handleBackdropKeyDown);

        return () => {
            dialogEl.removeEventListener('cancel', handleCancel);
            dialogEl.removeEventListener('click', handleBackdropClick);
            dialogEl.removeEventListener('keydown', handleBackdropKeyDown);
        };
    }, [onCancel]);

    if (!isOpen) return null;

    return createPortal(
        <dialog
            ref={dialogRef}
            className="confirmation-modal-content"
            aria-labelledby="confirmation-modal-title"
        >
            <div className="confirmation-modal-header">
                <h3 id="confirmation-modal-title">{title}</h3>
            </div>
            <div className="confirmation-modal-body">
                <p>{message}</p>
            </div>
            <div className="confirmation-modal-actions">
                <button
                    className="btn-confirmation-cancel"
                    onClick={onCancel}
                >
                    {cancelText}
                </button>
                <button
                    className={`btn-confirmation-confirm ${isDestructive ? 'destructive' : ''}`}
                    onClick={onConfirm}
                >
                    {confirmText}
                </button>
            </div>
        </dialog>,
        document.body
    );
};

export default ConfirmationModal;
